import React, { useEffect, useRef } from 'react';
import { Box, Text } from '@chakra-ui/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { approximateBoundaryRing } from '../../utils/geocode';

const pinIcon = L.divIcon({
  className: 'utkarsh-map-pin',
  html: `<div style="
    width:16px;height:16px;border-radius:50%;
    background:#F9B101;border:2px solid #141414;
    box-shadow:0 1px 4px rgba(0,0,0,.35);
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const BORDER_STYLE = {
  color: '#C62828',
  weight: 3,
  dashArray: '8 6',
  fillColor: '#C62828',
  fillOpacity: 0.08,
};

const DEFAULT_CENTER = [22.839, 69.721];
const COORDS_EPS = 1e-5;

function sameCoords(aLat, aLng, bLat, bLng) {
  return (
    Math.abs(Number(aLat) - Number(bLat)) < COORDS_EPS &&
    Math.abs(Number(aLng) - Number(bLng)) < COORDS_EPS
  );
}

/**
 * Village map — search result / center-pin pick / optional boundary.
 * When `clickable`, matches mobile: pin stays at viewport center while the map
 * pans; address resolve is driven by drag/move (not only tap).
 */
export default function VillageMap({
  latitude,
  longitude,
  boundary = null,
  clickable = false,
  onPick,
  height = '280px',
  hint,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const borderRef = useRef(null);
  const onPickRef = useRef(onPick);
  /** Count of upcoming moveend events caused by our own setView/invalidateSize. */
  const programmaticMoveRef = useRef(0);
  const userDraggingRef = useRef(false);
  const dragThrottleRef = useRef(null);
  const hasCenteredOnceRef = useRef(false);
  onPickRef.current = onPick;

  const emitCenter = (map) => {
    const c = map.getCenter();
    onPickRef.current?.(c.lat, c.lng);
  };

  const markProgrammatic = () => {
    programmaticMoveRef.current += 1;
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const startLat =
      latitude != null && !Number.isNaN(Number(latitude))
        ? Number(latitude)
        : DEFAULT_CENTER[0];
    const startLng =
      longitude != null && !Number.isNaN(Number(longitude))
        ? Number(longitude)
        : DEFAULT_CENTER[1];

    const map = L.map(containerRef.current, {
      center: [startLat, startLng],
      zoom: latitude != null ? 13 : 11,
      scrollWheelZoom: false,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    if (clickable) {
      markProgrammatic(); // swallow init moveend

      map.on('click', (e) => {
        // Pan only — keep current zoom (mobile parity).
        markProgrammatic();
        map.setView(e.latlng, map.getZoom(), { animate: true });
        // Emit immediately so reverse-geocode starts without waiting for moveend.
        onPickRef.current?.(e.latlng.lat, e.latlng.lng);
      });

      map.on('dragstart', () => {
        userDraggingRef.current = true;
      });

      map.on('drag', () => {
        // Live updates while dragging (throttled) — same as mobile onPositionChanged.
        if (dragThrottleRef.current) return;
        dragThrottleRef.current = setTimeout(() => {
          dragThrottleRef.current = null;
          emitCenter(map);
        }, 80);
      });

      map.on('dragend', () => {
        userDraggingRef.current = false;
        if (dragThrottleRef.current) {
          clearTimeout(dragThrottleRef.current);
          dragThrottleRef.current = null;
        }
        emitCenter(map);
      });

      map.on('moveend', () => {
        if (programmaticMoveRef.current > 0) {
          programmaticMoveRef.current -= 1;
          return;
        }
        // Skip if dragend already emitted (avoid double fire).
        if (userDraggingRef.current) return;
        emitCenter(map);
      });
    }

    mapRef.current = map;
    hasCenteredOnceRef.current = latitude != null && longitude != null;

    const invalidate = () => {
      if (clickable) markProgrammatic();
      map.invalidateSize({ animate: false });
    };
    const t1 = setTimeout(invalidate, 50);
    const t2 = setTimeout(invalidate, 250);
    window.addEventListener('resize', invalidate);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (dragThrottleRef.current) clearTimeout(dragThrottleRef.current);
      window.removeEventListener('resize', invalidate);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      borderRef.current = null;
      hasCenteredOnceRef.current = false;
      userDraggingRef.current = false;
      programmaticMoveRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickable]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (borderRef.current) {
      map.removeLayer(borderRef.current);
      borderRef.current = null;
    }
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }

    if (latitude == null || longitude == null || Number.isNaN(Number(latitude))) {
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    let layer = null;
    if (boundary && (boundary.type === 'Polygon' || boundary.type === 'MultiPolygon')) {
      layer = L.geoJSON(boundary, { style: () => BORDER_STYLE });
    } else {
      layer = L.polygon(approximateBoundaryRing(lat, lng), BORDER_STYLE);
    }
    layer.addTo(map);
    borderRef.current = layer;

    if (clickable) {
      // Don't fight the user while they drag; only pan for external updates
      // (e.g. Google Places search).
      if (userDraggingRef.current) {
        hasCenteredOnceRef.current = true;
      } else {
        const center = map.getCenter();
        if (!sameCoords(center.lat, center.lng, lat, lng)) {
          markProgrammatic();
          map.setView([lat, lng], map.getZoom() || 13, {
            animate: !hasCenteredOnceRef.current,
          });
        }
        hasCenteredOnceRef.current = true;
      }
    } else {
      markerRef.current = L.marker([lat, lng], { icon: pinIcon }).addTo(map);
      try {
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds.pad(0.2));
        } else {
          map.setView([lat, lng], 13);
        }
      } catch {
        map.setView([lat, lng], 13);
      }
    }

    const t = setTimeout(() => {
      if (clickable) markProgrammatic();
      map.invalidateSize({ animate: false });
    }, 80);
    return () => clearTimeout(t);
  }, [latitude, longitude, boundary, clickable]);

  return (
    <Box position="relative" zIndex={0} isolation="isolate">
      {hint ? (
        <Text fontSize="13px" color="text.secondary" mb={2}>
          {hint}
        </Text>
      ) : null}
      <Box
        ref={containerRef}
        h={height}
        w="100%"
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
        overflow="hidden"
        bg="bg.input"
        position="relative"
        zIndex={0}
        sx={{
          '.leaflet-container': {
            height: '100%',
            width: '100%',
            font: 'inherit',
            zIndex: 0,
          },
          '.leaflet-pane, .leaflet-control, .leaflet-top, .leaflet-bottom': {
            zIndex: '1 !important',
          },
          '.leaflet-control-attribution': {
            fontSize: '10px',
          },
        }}
      >
        {clickable ? (
          <Box
            pointerEvents="none"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -100%)"
            zIndex={2}
            lineHeight={0}
            aria-hidden
          >
            <Box
              as="span"
              display="block"
              w="28px"
              h="28px"
              borderRadius="full"
              bg="utkarsh.amber"
              borderWidth="2px"
              borderColor="utkarsh.ink"
              boxShadow="0 2px 8px rgba(0,0,0,.35)"
              position="relative"
              _after={{
                content: '""',
                position: 'absolute',
                left: '50%',
                bottom: '-6px',
                transform: 'translateX(-50%)',
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '8px solid',
                borderTopColor: 'utkarsh.amber',
              }}
            />
          </Box>
        ) : null}
      </Box>
      {latitude != null && longitude != null ? (
        <Text fontSize="12px" color="text.secondary" mt={2} fontFamily="mono">
          Center {Number(latitude).toFixed(5)}, {Number(longitude).toFixed(5)}
          {boundary
            ? ' · official village border'
            : ' · approx. border (no official polygon)'}
        </Text>
      ) : null}
    </Box>
  );
}
